import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { DropResult } from "react-beautiful-dnd";

import { showErrorToast } from "@components/Common/ErrorToast";
import { RootState } from "../../../../../../redux/store";
import { setPreviewLanguage } from "../../../../../../redux/scanToSlice";
import { ScanToContext } from "..";
import { RubricResetButton } from "../shared/RubricResetButton";

import { PropsType, AdItem } from "./types";
import {
    DEFAULT_SECTION_TITLES,
    SECTION_ICONS,
    LANGUAGE_CODES,
    DEFAULT_ADS,
    DEFAULT_AD_IMAGES,
} from "./constants";
import {
    CUSTOM_ICON_KEY,
    DISPLAY_TYPE_MOBILE,
    DISPLAY_KEY_MOBILE,
    DISPLAY_KEY_DESKTOP,
    SCAN_TO_DEFAULT_MAIN_COLOR,
} from "../shared/constants";
import { useMultiCustomIcons } from "../shared/useMultiCustomIcons";
import {
    Container,
    LeftSection,
    SectionHeader,
    PublicitesHeaderGroup,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    Toggle,
} from "./styled";
import { SectionTitleCard } from "./SectionTitleCard";
import { SectionIconCard } from "../shared/SectionIconCard";
import { AdsTable } from "./AdsTable";
import { DeleteConfirmModal } from "./DeleteConfirmModal";

// ─── Component ──────────────────────────────────────────

export function Publicites({ rowData }: PropsType): JSX.Element {
    const { t } = useTranslation();
    const mainColor =
        useSelector((state: RootState) => state.global.infoUser.maincolor) ||
        SCAN_TO_DEFAULT_MAIN_COLOR;

    const {
        setSharedPublicitesData,
        registerPendingFile,
        removePendingFile,
        registerDeletedImage,
        broadcastChannelRef,
    } = React.useContext(ScanToContext);

    // ─── Languages (translated labels) ───────────────────
    const languages = React.useMemo(
        () =>
            LANGUAGE_CODES.map((l) => ({ code: l.code, label: t(l.labelKey) })),
        [t]
    );

    // ─── Section icons (translated labels) ───────────────
    const sectionIcons = React.useMemo(
        () =>
            SECTION_ICONS.map((i) => ({
                key: i.key,
                label: t(i.labelKey),
                path: i.path,
            })),
        [t]
    );

    // ─── Default section titles from constants ───────────
    const defaultSectionTitles = DEFAULT_SECTION_TITLES;

    // ─── State ───────────────────────────────────────────
    // Cast: AdvertisingConfig type doesn't declare every UI-state field used at
    // runtime (selectedLanguage, sectionTitles, etc.). Out of scope for ARCH-02.
    const savedAds = useSelector(
        (state: RootState) => state.scanTo?.advertising
    ) as any;
    const [adsEnabled, setAdsEnabled] = React.useState(
        savedAds?.adsEnabled ?? true
    );
    const [sectionTitleVisible, setSectionTitleVisible] = React.useState(
        savedAds?.sectionTitleVisible ?? true
    );
    // Editing + preview share ONE language: the shared previewLanguage, so
    // picking the section-title language here flips every section of the
    // preview. Aliased so existing call sites keep working unchanged.
    const dispatch = useDispatch();
    const previewLanguage = useSelector(
        (state: RootState) => state.scanTo.previewLanguage
    );
    const selectedLanguage = previewLanguage;
    const setSelectedLanguage = React.useCallback(
        (lang: string) => dispatch(setPreviewLanguage(lang)),
        [dispatch]
    );
    const [selectedIcon, setSelectedIcon] = React.useState(
        savedAds?.selectedIcon || "tag"
    );
    // Multi-icon state lifted into the shared hook so all 4 tabs
    // (Offres / Actions / MaCommande / Publicites) share one source of
    // truth — see `shared/useMultiCustomIcons.ts`.
    const {
        customIconUrls,
        customIconFilenames,
        selectedCustomIdx,
        setSelectedCustomIdx,
        customIconUrl,
        iconFileInputRef,
        handleIconUpload,
        removeCustomIcon,
        resetCustomIcons,
    } = useMultiCustomIcons({
        saved: savedAds,
        fallbackIconKey: "tag",
        setSelectedIcon,
    });
    const [sectionTitles, setSectionTitles] = React.useState<
        Record<string, string>
    >(savedAds?.sectionTitles || { ...defaultSectionTitles });
    const [displayType, setDisplayType] = React.useState(
        savedAds?.displayType || "desktop"
    );
    const [ads, setAds] = React.useState<{
        Mobile: AdItem[];
        Desktop: AdItem[];
    }>(() => {
        const raw = savedAds?.fullAds || savedAds?.ads;
        if (Array.isArray(raw)) {
            return {
                Mobile: [...DEFAULT_ADS],
                Desktop: raw.length > 0 ? raw : [...DEFAULT_ADS],
            };
        }
        if (raw && raw.Mobile !== undefined) {
            return {
                Mobile: raw.Mobile.length > 0 ? raw.Mobile : [...DEFAULT_ADS],
                Desktop:
                    raw.Desktop.length > 0 ? raw.Desktop : [...DEFAULT_ADS],
            };
        }
        return { Mobile: [...DEFAULT_ADS], Desktop: [...DEFAULT_ADS] };
    });

    const displayKey =
        displayType === DISPLAY_TYPE_MOBILE
            ? DISPLAY_KEY_MOBILE
            : DISPLAY_KEY_DESKTOP;
    const currentAds = ads[displayKey];
    const setCurrentAds = (updated: AdItem[]) => {
        setAds({ ...ads, [displayKey]: updated });
    };
    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
    const [adToDelete, setAdToDelete] = React.useState<number | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    // Active ads for preview
    const activeAds = React.useMemo(
        () =>
            [...currentAds]
                .filter((ad) => ad.active)
                .sort((a, b) => a.order - b.order),
        [currentAds]
    );

    // ─── Register default ad images as pending files for upload on first save ───
    React.useEffect(() => {
        const defaultFilenames: Record<number, string> = {
            1: "pub_pizza.jpg",
            2: "pub_gastronomie.jpg",
            3: "pub_grillades.jpg",
        };
        Object.entries(DEFAULT_AD_IMAGES).forEach(([idStr, src]) => {
            const id = Number(idStr);
            // Skip if the ad no longer exists in state OR already has a blob/http URL
            const ad = [...ads.Mobile, ...ads.Desktop].find((a) => a.id === id);
            if (!ad) return;
            if (
                ad.imageUrl &&
                (ad.imageUrl.startsWith("blob:") ||
                    ad.imageUrl.startsWith("http"))
            )
                return;

            fetch(src)
                .then((res) => res.blob())
                .then((blob) => {
                    const filename = defaultFilenames[id] || `ad_${id}.jpg`;
                    const file = new File([blob], filename, {
                        type: blob.type,
                    });
                    const blobUrl = URL.createObjectURL(blob);
                    registerPendingFile(blobUrl, file);
                    setAds((prev) => ({
                        Mobile: prev.Mobile.map((a) =>
                            a.id === id
                                ? { ...a, imageUrl: blobUrl, filename }
                                : a
                        ),
                        Desktop: prev.Desktop.map((a) =>
                            a.id === id
                                ? { ...a, imageUrl: blobUrl, filename }
                                : a
                        ),
                    }));
                })
                .catch(() => {});
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Share publicites preview data via context + BroadcastChannel
    React.useEffect(() => {
        const previewData = {
            adsEnabled,
            sectionTitleVisible,
            sectionTitle: sectionTitles[selectedLanguage],
            sectionIconPath:
                selectedIcon !== CUSTOM_ICON_KEY
                    ? sectionIcons.find((i) => i.key === selectedIcon)?.path ||
                      sectionIcons[0].path
                    : "",
            customIconUrl:
                selectedIcon === CUSTOM_ICON_KEY ? customIconUrl : "",
            ads: activeAds.map((ad) => ({
                id: ad.id,
                imageUrl:
                    ad.imageUrl ||
                    (ad.filename?.startsWith("http") ? ad.filename : ""),
            })),
            // Full config for Redux persistence
            sectionTitles,
            selectedIcon,
            customIconUrls,
            customIconFiles: customIconUrls.map((url, i) => ({
                filename: customIconFilenames[i] || "",
                url,
            })),
            selectedCustomIdx,
            displayType,
            fullAds: ads,
        };

        // Share with other same-page tabs via context
        setSharedPublicitesData(previewData);

        // Send to external preview window via the centralized BroadcastChannel
        if (broadcastChannelRef.current) {
            try {
                broadcastChannelRef.current.postMessage({
                    source: "publicites",
                    ...previewData,
                });
            } catch (_) {
                /* channel closed */
            }
        }

        // Persist for external window initial load
        sessionStorage.setItem(
            "scanToPublicitesPreviewData",
            JSON.stringify(previewData)
        );
    }, [
        adsEnabled,
        sectionTitleVisible,
        sectionTitles,
        selectedIcon,
        customIconUrl,
        customIconUrls,
        customIconFilenames,
        selectedCustomIdx,
        selectedLanguage,
        activeAds,
        ads,
        displayType,
        broadcastChannelRef,
        setSharedPublicitesData,
        sectionIcons,
    ]);

    // Cleanup blob URLs on component unmount.
    // Track the latest ads in a ref so the cleanup effect can read them at
    // unmount without re-running (and revoking still-active URLs) on every
    // change to `currentAds`.
    const currentAdsRef = React.useRef(currentAds);
    React.useEffect(() => {
        currentAdsRef.current = currentAds;
    }, [currentAds]);

    React.useEffect(() => {
        return () => {
            currentAdsRef.current.forEach((ad) => {
                if (ad.imageUrl.startsWith("blob:")) {
                    URL.revokeObjectURL(ad.imageUrl);
                }
            });
        };
    }, []);

    // (Custom icon blob URL cleanup is now inside useMultiCustomIcons.)

    // Handle drag and drop
    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        if (sourceIndex === destinationIndex) return;

        const reorderedAds = Array.from(currentAds);
        const [movedAd] = reorderedAds.splice(sourceIndex, 1);
        reorderedAds.splice(destinationIndex, 0, movedAd);

        const updatedAds = reorderedAds.map((ad, index) => ({
            ...ad,
            order: index + 1,
        }));

        setCurrentAds(updatedAds);
    };

    // Handle manual order change
    const handleOrderChange = (adId: number, newOrder: string) => {
        const orderNum = parseInt(newOrder, 10);
        if (isNaN(orderNum) || orderNum < 1 || orderNum > currentAds.length)
            return;

        const currentAd = currentAds.find((ad) => ad.id === adId);
        if (!currentAd) return;

        const currentIndex = currentAds.findIndex((ad) => ad.id === adId);
        const newIndex = orderNum - 1;

        if (currentIndex === newIndex) return;

        const reorderedAds = Array.from(currentAds);
        reorderedAds.splice(currentIndex, 1);
        reorderedAds.splice(newIndex, 0, currentAd);

        const updatedAds = reorderedAds.map((ad, index) => ({
            ...ad,
            order: index + 1,
        }));

        setCurrentAds(updatedAds);
    };

    // Toggle individual ad
    const toggleAdActive = (adId: number) => {
        setCurrentAds(
            currentAds.map((ad) =>
                ad.id === adId ? { ...ad, active: !ad.active } : ad
            )
        );
    };

    // View image
    const handleViewImage = (adId: number) => {
        const ad = currentAds.find((a) => a.id === adId);
        const url =
            ad?.imageUrl ||
            (ad?.filename?.startsWith("http") ? ad.filename : "");
        if (url) {
            window.open(url, "_blank");
        }
    };

    // Delete handling
    const handleDeleteClick = (adId: number) => {
        setAdToDelete(adId);
        setDeleteConfirmOpen(true);
    };

    const confirmDelete = () => {
        if (adToDelete !== null) {
            const ad = currentAds.find((a) => a.id === adToDelete);
            if (ad?.filename) registerDeletedImage(ad.filename);
            if (ad?.imageUrl?.startsWith("blob:")) {
                removePendingFile(ad.imageUrl);
                URL.revokeObjectURL(ad.imageUrl);
            }
            setCurrentAds(currentAds.filter((a) => a.id !== adToDelete));
            setDeleteConfirmOpen(false);
            setAdToDelete(null);
        }
    };

    const cancelDelete = () => {
        setDeleteConfirmOpen(false);
        setAdToDelete(null);
    };

    // Image upload
    const handleAddButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const validTypes = ["image/png", "image/jpeg", "image/jpg"];
        if (!validTypes.includes(file.type)) {
            showErrorToast(
                t("Invalid file format. Please use PNG, JPG or JPEG.")
            );
            return;
        }

        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            showErrorToast(t("File size is too large. Maximum 5MB."));
            return;
        }

        const imageUrl = URL.createObjectURL(file);
        registerPendingFile(imageUrl, file);
        const allIds = [...ads.Mobile, ...ads.Desktop].map((a) => a.id);
        const newAdId = Math.max(...allIds, 0) + 1;
        const newAd: AdItem = {
            id: newAdId,
            order: currentAds.length + 1,
            filename: file.name,
            imageUrl,
            active: true,
        };

        setCurrentAds([...currentAds, newAd]);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // Section title handlers
    const resetSectionTitle = () => {
        setSectionTitles({
            ...sectionTitles,
            [selectedLanguage]: defaultSectionTitles[selectedLanguage],
        });
    };

    const resetAllSectionTitles = () => {
        setSectionTitles({ ...defaultSectionTitles });
        setSectionTitleVisible(true);
        setSelectedLanguage("fr");
    };

    // Reset just the advertising images back to the default set (no
    // confirmation). DEFAULT_ADS already carry their imageUrl, so they show
    // immediately; the broadcast effect syncs Redux + preview.
    const resetAds = () => {
        setAds({
            Mobile: DEFAULT_ADS.map((a) => ({ ...a })),
            Desktop: DEFAULT_ADS.map((a) => ({ ...a })),
        });
    };

    // Reset the WHOLE advertising rubric to defaults, directly on local state.
    const resetAdvertisingAll = () => {
        setAdsEnabled(true);
        setDisplayType("desktop");
        setSelectedIcon("tag");
        resetCustomIcons();
        resetAllSectionTitles();
        resetAds();
    };

    // (handleIconUpload + removeCustomIcon are provided by useMultiCustomIcons.)

    return (
        <Container data-testid="scan-to-ads-container">
            <LeftSection data-testid="scan-to-ads-left-section">
                {/* Toggle activation + whole-rubric reset */}
                <PublicitesHeaderGroup>
                    <SectionHeader>
                        <SectionTitle>{t("Advertising")}</SectionTitle>
                        <ToggleContainer>
                            <ToggleLabel>
                                {t("Disable advertising")}
                            </ToggleLabel>
                            <Toggle
                                data-testid="scan-to-ads-toggle-enabled"
                                isActive={adsEnabled}
                                mainColor={mainColor}
                                onClick={() => setAdsEnabled(!adsEnabled)}
                                aria-label={t("Disable advertising")}
                            />
                        </ToggleContainer>
                    </SectionHeader>
                    <RubricResetButton
                        section="advertising"
                        onReset={resetAdvertisingAll}
                    />
                </PublicitesHeaderGroup>

                {/* Section title (multilingual) */}
                <SectionTitleCard
                    mainColor={mainColor}
                    languages={languages}
                    selectedLanguage={selectedLanguage}
                    onLanguageChange={setSelectedLanguage}
                    defaultSectionTitles={defaultSectionTitles}
                    sectionTitles={sectionTitles}
                    onSectionTitleChange={(lang, value) =>
                        setSectionTitles({
                            ...sectionTitles,
                            [lang]: value,
                        })
                    }
                    sectionTitleVisible={sectionTitleVisible}
                    onTitleVisibleToggle={() =>
                        setSectionTitleVisible(!sectionTitleVisible)
                    }
                    onResetSectionTitle={resetSectionTitle}
                    onResetAllSectionTitles={resetAllSectionTitles}
                />

                {/* Section icon */}
                <SectionIconCard
                    mode="multi"
                    testIdPrefix="scan-to-ads"
                    mainColor={mainColor}
                    sectionIcons={sectionIcons}
                    selectedIcon={selectedIcon}
                    onIconSelect={setSelectedIcon}
                    customIconUrls={customIconUrls}
                    selectedCustomIdx={selectedCustomIdx}
                    onSelectCustomIcon={(idx) => {
                        setSelectedCustomIdx(idx);
                        setSelectedIcon(CUSTOM_ICON_KEY);
                    }}
                    iconFileInputRef={iconFileInputRef}
                    onIconUpload={handleIconUpload}
                    onRemoveCustomIcon={removeCustomIcon}
                />

                {/* Ads table */}
                <AdsTable
                    mainColor={mainColor}
                    displayType={displayType}
                    onDisplayTypeChange={setDisplayType}
                    currentAds={currentAds}
                    fileInputRef={fileInputRef}
                    onImageUpload={handleImageUpload}
                    onAddButtonClick={handleAddButtonClick}
                    onDragEnd={handleDragEnd}
                    onOrderChange={handleOrderChange}
                    onToggleAdActive={toggleAdActive}
                    onViewImage={handleViewImage}
                    onDeleteClick={handleDeleteClick}
                    onResetImages={resetAds}
                />
            </LeftSection>

            {/* Delete Confirmation Modal */}
            {deleteConfirmOpen && (
                <DeleteConfirmModal
                    mainColor={mainColor}
                    onCancel={cancelDelete}
                    onConfirm={confirmDelete}
                />
            )}
        </Container>
    );
}
